<script setup lang="ts">
import type {
  AutoCompleteOption,
  AutoCompleteOptions
} from 'naive-ui/es/auto-complete/src/interface'
import type { VNodeChild } from 'vue'

const props = defineProps<{
  modelValue: string
  size?: 'small' | 'medium' | 'large'
  self?: boolean
}>()

const emit = defineEmits(['update:modelValue', 'submit'])
const { addToHistory } = useSearchHistory()
const options = ref<AutoCompleteOptions>([])

const value = computed({
  get() {
    return props.modelValue
  },
  set(value: string) {
    emit('update:modelValue', value)
    searchTips(value)
  }
})

function heatMapColor(heatValue: number): string {
  const scaledSaturationValue =
    (Math.log(heatValue + 1) / Math.log(100 + 1)) * 100
  const saturation = Math.min(100, Math.max(5, scaledSaturationValue))

  const decayFactor = 0.05
  const scaledLightnessValue = 50 + 50 * Math.exp(-decayFactor * heatValue)
  const lightness = Math.min(90, Math.max(50, scaledLightnessValue))
  const logHue = 180 - (Math.log(heatValue + 1) / Math.log(100 + 1)) * 180
  const finalHue = Math.max(0, logHue)

  return `hsl(${finalHue}deg, ${saturation}%, ${lightness}%)`
}

function renderLabel(option: AutoCompleteOption): VNodeChild {
  return [
    h('i', {
      class: 'icon bi bi-fire',
      style: {
        color: heatMapColor(option.hot as number)
      }
    }),
    h('span', {
      class: 'highlight',
      innerHTML: highlight(option.label as string)
    })
  ]
}

function highlight(text?: string | null) {
  if (!text) return '' // Return empty string if text is null or undefined
  const escapedText = text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') // Escape special characters
  const reg = new RegExp(escapedText.trim(), 'gi')
  return text.replace(reg, (match) => `<em>${match}</em>`)
}

async function searchTips(name: string) {
  if (!name) return
  try {
    const res = await fetchTagByName(name)
    const list: any[] = []
    res.map((t) => {
      let index = list.findIndex(
        (item) =>
          (item as any).value.toLocaleLowerCase() === t.name.toLocaleLowerCase()
      )
      if (index !== -1) {
        list[index].hot += t.hot
      } else {
        list.push({
          label: t.name.toLocaleLowerCase(),
          value: t.name.toLocaleLowerCase(),
          class: 'm-search-input-tip',
          hot: t.hot
        })
      }
    })

    options.value = list.sort((a, b) => (b.hot as number) - (a.hot as number))
    // 从options.value中找到label和name一样的，放到首位，如果没有，则新增一个，热度设置为0
    let index = options.value.findIndex((t) => (t as any).value === name)
    if (index !== -1) {
      let item = options.value.splice(index, 1)[0]
      options.value.unshift(item as any)
    } else {
      options.value.unshift({
        label: name,
        value: name,
        class: 'm-search-input-tip',
        hot: 0
      })
    }
  } catch (error) {
    console.error(error)
  }
}

function handleClick(e: Event) {
  e.preventDefault()
  e.stopPropagation()
  submit()
}

function handleSelect(text: string) {
  value.value = text
}

function handleKeydown(e: KeyboardEvent) {
  // 如果正在使用输入法（拼音组合中），忽略回车
  if (e.isComposing || e.keyCode === 229) {
    return
  }
  nextTick(() => {
    submit()
  })
}

function submit() {
  const text = value.value.trim()
  if (text) {
    addToHistory(text)
  }
  const route = useRoute()
  route.query.q = text
  const queryString = Object.keys(route.query)
    .map((key) => `${key}=${encodeURIComponent(route.query[key] as string)}`)
    .join('&')
  const url = `/search?${queryString}`
  if (props.self) {
    window.location.href = url
  } else {
    open(url)
  }
}

function preventDefault(e: Event) {
  e.preventDefault()
}
</script>

<template>
  <n-auto-complete
    ref="autoCompleteRef"
    v-model:value="value"
    to="body"
    :input-props="{
      autocomplete: 'disabled'
    }"
    :options="options"
    :render-label="renderLabel"
    :size="size"
    :on-select="handleSelect"
    class="m-search-input"
    placeholder="请输入搜索内容喵"
    clearable
    @keydown.enter="handleKeydown"
    @keydown.up="preventDefault"
    @keydown.down="preventDefault"
  >
    <template #suffix>
      <n-button
        v-if="size === 'large'"
        type="primary"
        class="m-search-input-button"
        @click="handleClick"
      >
        搜索
      </n-button>
      <n-button v-else quaternary aria-label="搜索" @click="submit">
        <m-icon icon="lucide-search" />
      </n-button>
    </template>
  </n-auto-complete>
</template>

<style lang="scss">
.m-search-input {
  .n-input .n-input-wrapper {
    padding-right: 4px;
  }
  &-button {
    margin-left: 12px;
    width: 120px;
  }
  &-tip {
    width: 100%;
    display: flex;
    justify-content: space-between;
    background-color: #fff;
    .icon {
      margin-right: 8px;
    }
  }
}
</style>
